import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { Flex, Heading, Text, Icon } from '@qga/roo-ui/components';
import { useSelector } from 'react-redux';
import { themeGet } from 'styled-system';
import PointsEarnSummary from 'components/PointsEarnSummary';
import Truncate from 'components/Truncate';
import PropertyRatings from 'components/PropertyRatings';
import OfferBreakdown from './OfferBreakdown';
import CampaignPriceMessage from 'components/CampaignPriceMessage';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getPropertyLinkQuery } from 'store/search/searchSelectors';
import stringifyQueryValues from 'lib/search/stringifyQueryValues';
import { getIsMobileApp } from 'store/ui/uiSelectors';
import { POINTS_PER_DOLLAR_DEFAULT, POINTS_EARN_ENABLED } from 'config';
import PointsPerDollar from 'components/PointsPerDollar';
import { InlineCard, StackedCard } from './StyledCard';
import { PropertyCardProps } from 'types/property';
import { getQueryCheckIn } from 'store/router/routerSelectors';
import useViewPromotionEvent from 'hooks/useViewPromotionEvent';
import useSelectPromotionEvent from 'hooks/useSelectPromotionEvent';
import useStandardCampaign from 'hooks/optimizely/useStandardCampaign';
import { getCampaignDisclaimerAndPromotionMessage } from 'lib/campaigns/helpers';
import { getCampaignDefaultSash } from 'store/campaign/campaignSelectors';

const PropertyName = styled(Heading.h2)`
  ${themeGet('card.propertyCard.propertyName')};
`;

const OfferBreakdownText = styled(Text)`
  ${themeGet('card.propertyCard.offerBreakdown')};
`;

const FreeCancellationWrapper = styled(Flex)`
  ${themeGet('card.propertyCard.freeCancellationWrapper')};
`;

const PropertyCard = ({
  id: propertyId,
  inline,
  propertyName,
  rating,
  ratingType,
  customerRating,
  total,
  checkIn,
  checkOut,
  isNonRefundable,
  pointsEarned,
  offerType,
  imageSrc,
  imageSrcSet,
  imageAltTag,
  promotionName = '',
  onClick,
  showCampaignMessage = true,
  showRatingTooltip = true,
  featuredOfferId,
  recommended = false,
  isPromoCard = false,
  displayInTotalPointsEarned = false,
  address,
  ...rest
}: PropertyCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);
  const isMobileOrTablet = isLessThanBreakpoint(1);
  const isMobileApp = useSelector(getIsMobileApp);
  const checkInDate = useSelector(getQueryCheckIn);
  const today = new Date();
  const isCheckInDateToday = today.setHours(0, 0, 0, 0) === checkInDate?.setHours(0, 0, 0, 0);
  const isClassic = offerType === 'classic';
  const shouldOpenInSameTab = isMobileOrTablet || isMobileApp;
  const propertyLinkQuery = useSelector(getPropertyLinkQuery);

  const promotion = { name: promotionName, slot: 'property_card', assetName: propertyName };
  const { fireSelectPromotionEvent } = useSelectPromotionEvent({ promotion });
  useViewPromotionEvent({ ref, promotion });

  const { isStandardCampaignEnabled, isGeoTargeted, campaignGeoConfig } = useStandardCampaign();
  const globalCampaignDefaultSash = useSelector(getCampaignDefaultSash);

  const { disclaimerSymbol, campaignSpecificPromotionMessage } = getCampaignDisclaimerAndPromotionMessage(
    isStandardCampaignEnabled,
    address,
    isGeoTargeted,
    campaignGeoConfig,
  );
  const campaignMessage = globalCampaignDefaultSash ? globalCampaignDefaultSash : campaignSpecificPromotionMessage;

  if (!propertyId || !propertyName) return null;
  const updatedPropertyLinkQuery = {
    ...propertyLinkQuery,
    ...(featuredOfferId && {
      featuredOfferId,
      payWith: offerType === 'classic' ? 'points' : 'cash',
    }),
    ...(recommended && { recommended: 'true' }),
  };
  const propertyLinkQueryString = stringifyQueryValues(updatedPropertyLinkQuery);
  const propertyUrl = `/properties/${propertyId}?${propertyLinkQueryString}`;

  const Component = inline ? InlineCard : StackedCard;
  const currency = total.currency;
  const isCurrencyCash = total.currency !== 'PTS';
  const pointsPerDollar = pointsEarned?.maxQffEarnPpd ? pointsEarned.maxQffEarnPpd : POINTS_PER_DOLLAR_DEFAULT;

  const handleClick = () => {
    fireSelectPromotionEvent();
    onClick && onClick();
  };

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <Component
        compact={isMobile}
        imageAltTag={imageAltTag}
        imageSrc={imageSrc}
        imageSrcSet={imageSrcSet}
        onClick={handleClick}
        promotionName={campaignMessage && !isClassic ? campaignMessage : promotionName}
        target={shouldOpenInSameTab ? '_self' : '_blank'}
        to={propertyUrl}
        isClassic={isClassic}
        isPromoCard={isPromoCard}
        {...rest}
      >
        <Text color="red">{showCampaignMessage}</Text>
        <Flex flexDirection="column" justifyContent="space-between" height="100%">
          <Flex flexDirection="column">
            <Truncate textAlign="left" lines={2} mt={1}>
              <PropertyName lineHeight="1.25" data-testid="property-name">
                {propertyName}
              </PropertyName>
            </Truncate>
            <Flex flexDirection="row" alignItems="center" my={2}>
              <PropertyRatings
                hotelRating={rating}
                hotelRatingType={ratingType}
                hotelRatingSize={12}
                customerRating={customerRating}
                showTooltip={showRatingTooltip}
              />
            </Flex>
          </Flex>

          <Flex alignItems="flex-end" justifyContent="flex-end" flexDirection="column">
            <OfferBreakdown offerType={offerType} total={total} checkIn={checkIn} checkOut={checkOut} />
            {showCampaignMessage && <CampaignPriceMessage currency={currency} offerType={offerType} fallback={isClassic} />}
            {!featuredOfferId &&
              POINTS_EARN_ENABLED &&
              isCurrencyCash &&
              (displayInTotalPointsEarned ? (
                <PointsEarnSummary {...pointsEarned.qffPoints} fontSize="sm" />
              ) : (
                <PointsPerDollar pointsPerDollar={pointsPerDollar} fontSize={['xs', 'sm']} disclaimerSymbol={disclaimerSymbol} />
              ))}
            {featuredOfferId && isCurrencyCash && <PointsEarnSummary {...pointsEarned.qffPoints} fontSize="sm" />}
            <FreeCancellationWrapper alignItems="flex-end" pb={isNonRefundable ? '22px' : '0px'}>
              {!isNonRefundable && !isCheckInDateToday && (
                <>
                  <Icon name="freeCancellation" color="darkGreen" size={[18, 22]} mr={2} />
                  <OfferBreakdownText color="darkGreen" display="block" data-testid="free-cancellation-text">
                    Free cancellation
                  </OfferBreakdownText>
                </>
              )}
            </FreeCancellationWrapper>
          </Flex>
        </Flex>
      </Component>
    </div>
  );
};

export default PropertyCard;
